import { createSelector } from '@reduxjs/toolkit';

import { ZeroResultsContainerCategory } from '@/components/ZeroResults/ZeroResultsContainer';
import { getUserExcludedFacets } from '@/redux/modules/search/exclusions/searchExclusions.selectors';
import { GlobalState } from '@/redux/store';
import { FacetOption } from '@/types/FacetOption';
import FacetTypes from '@/types/search/enums/facetTypes';

// Search selectors can get very, very complex.
// Because of that, circular dependencies are going to be common.
// To avoid that, this file should contain selectors that pull from multiple slices.

const searchSliceSelector = ({ search }: GlobalState) => search;
const idSelector = (_: GlobalState, id: string) => id;

// search-party does not emit `excludable` / `excluded` in its payload, so this
// client-side annotation is what drives the "Exclude this auction house" menu
// in MultiCheckFacet and the disabled state for options the user has already excluded.
const annotateFacetExclusions = (facets: any[] = [], exclusions: Record<string, string[]> = {}) =>
    facets.map((facet) => {
        const facetExclusions = exclusions[facet.id];
        if (!facetExclusions?.length) {
            return facet;
        }
        return {
            ...facet,
            excludable: true,
            options: (facet.options ?? []).map((option: FacetOption) => ({
                ...option,
                excluded: facetExclusions.includes(option.id),
            })),
        };
    });

export const getSearchFacets = createSelector(searchSliceSelector, getUserExcludedFacets, (search, exclusions) =>
    annotateFacetExclusions(search.facets || [], exclusions)
);

/** Raw `priorityFacets` from the latest response. Always non-nil. */
export const getPriorityFacets = (state: GlobalState): string[] => state.search.priorityFacets;

/**
 * Sticky variant of `getPriorityFacets`. Falls back to the last non-empty list
 * captured for the current query session — keeps the pills visible while the
 * user refines a keyword search (filtered queries return `[]` by backend
 * contract). Always returns one of the two slice references, so referential
 * stability is preserved without `createSelector`. Always non-nil.
 */
export const getRenderedPriorityFacets = (state: GlobalState): string[] =>
    state.search.priorityFacets.length > 0 ? state.search.priorityFacets : state.search.stickyPriorityFacets;

const getAllCategories = (returnCategories: any[], facets: any[], level: number) => {
    const currentLevel = level + 1;
    facets.forEach(({ categories, count, id, name }) => {
        returnCategories.push({ count, id, level, name });
        if (categories) {
            getAllCategories(returnCategories, categories, currentLevel);
        }
    });
};

export const getSelectedOptionFacetValues = createSelector([idSelector, getSearchFacets], (id, facets) => {
    const facetValues = facets.find((facet) => facet.id === id);
    const selectedFacetsValues =
        facetValues && facetValues.options ? facetValues.options.find((facet) => facet.selected) : {};
    return selectedFacetsValues || {};
});

export const getSelectedFacetOptionsById = createSelector([idSelector, getSearchFacets], (id, facets) => {
    const facetValues = facets.find((facet) => facet.id === id);

    if (facetValues?.options) {
        return facetValues.options.filter((facet) => facet.selected);
    }
    return [];
});

export const getMergedOptionFacet = createSelector([idSelector, getSearchFacets], (id, facets) => {
    const facetValue = facets.find((facet) => facet.id === id);
    return facetValue || { options: [] };
});

export const getMergedRangeFacet = createSelector([idSelector, getSearchFacets], (id, facets) => {
    const facetValue = facets.find((facet) => facet.id === id);
    return {
        ...facetValue,
        range: facetValue?.range ?? { max: 0, min: 0 },
        selected: facetValue?.selected || false,
    };
});

export const getSelectedCategoryFacetValue = createSelector([idSelector, getSearchFacets], (id, facets) => {
    const facetValues = facets.find((facet) => facet.id === id);
    return facetValues?.categories?.facets?.[0] || {};
});

// No current `excludedFacets` key overlaps a sold-item facet id, so this annotation
// is a no-op today. Kept so `excludable` / `excluded` flags flow through automatically
// if a new exclusion type is introduced -- don't remove it thinking it's dead.
const getRecommendedSearchFacets = createSelector(searchSliceSelector, getUserExcludedFacets, (search, exclusions) =>
    annotateFacetExclusions(search.soldItemFacets || [], exclusions)
);

// Returns the selected creator option from soldItemFacets, or `undefined` when no
// creator facet exists or no option in it is selected. The `undefined`-on-empty
// shape keeps the return type symmetric with `.find()`'s natural contract and with
// the `FacetValue | undefined` declared in useOptimizedSearchSelectors.
export const getSoldFacetsSelectedCreator = createSelector(getRecommendedSearchFacets, (recommendedFacets) => {
    const facet = recommendedFacets?.find((x) => x.id === 'creator');
    return facet?.options.find((c) => c.selected);
});

export const getSoldItemImportantCreators = createSelector(
    [searchSliceSelector, getRecommendedSearchFacets],
    (search, recommendedFacets) => {
        const facet = recommendedFacets?.find((x) => x.id === 'creator');
        if (!facet) {
            return [];
        }
        const { totalSoldItems = 0 } = search;
        const { options: creators } = facet;
        return creators.filter((creator) => creator.count / totalSoldItems >= 0.1);
    }
);

export const getSoldItemImportantStylePeriod = createSelector(
    [searchSliceSelector, getRecommendedSearchFacets],
    (search, recommendedFacets) => {
        const facet = recommendedFacets?.find((x) => x.id === 'stylePeriod');
        if (!facet) {
            return [];
        }
        const { totalSoldItems = 0 } = search;
        const { options: creators } = facet;
        return creators.filter((creator) => creator.count / totalSoldItems >= 0.1);
    }
);

// F1 indicates category facet
export const getSoldItemImportantCategories = createSelector(
    [searchSliceSelector, getRecommendedSearchFacets],
    (search, recommendedFacets) => {
        const facet = recommendedFacets?.find((x) => x.id === 'F1');
        if (!facet) {
            return [];
        }
        const { categories: facets } = facet;
        const { totalSoldItems = 0 } = search;

        let categories: ZeroResultsContainerCategory[] = [];
        getAllCategories(categories, facets, 0);
        return categories
            .filter((category) => category.count / totalSoldItems >= 0.1)
            .sort((a, b) => b.count - a.count)
            .sort((a, b) => b.level - a.level);
    }
);

// F1 indicates category facet
export const getItemImportantCategories = createSelector(getSearchFacets, (searchFacets) => {
    const categoryFacet = searchFacets?.find((x) => x.id === 'F1');
    if (!categoryFacet) {
        return [];
    }

    const {
        categories: { facets },
    } = categoryFacet;

    let categories: ZeroResultsContainerCategory[] = [];
    getAllCategories(categories, facets, 0);
    return categories.sort((a, b) => b.count - a.count).sort((a, b) => b.level - a.level);
});

// F2 indicates origin facet
export const getSoldItemImportantOrigins = createSelector(
    [searchSliceSelector, getRecommendedSearchFacets],
    (search, recommendedFacets) => {
        const facet = recommendedFacets?.find((x) => x.id === 'F2');
        if (!facet) {
            return [];
        }
        const { categories: facets } = facet;
        const { totalSoldItems = 0 } = search;

        let categories: ZeroResultsContainerCategory[] = [];
        getAllCategories(categories, facets, 0);
        return categories
            .filter((category) => category.count / totalSoldItems >= 0.1)
            .sort((a, b) => b.count - a.count)
            .sort((a, b) => b.level - a.level);
    }
);

export const getUpcomingAuctionsCount = createSelector(getSearchFacets, (searchFacets) => {
    const facetValues = searchFacets.find((f) => f.id === FacetTypes.AUCTION_HOUSE_ID);
    return facetValues ? facetValues.options.length : 0;
});

export const getCategoryWithGreatestResults = createSelector(getSearchFacets, (searchFacets) => {
    const categoryFacets = searchFacets.find((f) => f.id === FacetTypes.CATEGORY_ID);
    return categoryFacets?.categories && categoryFacets.categories.facets.length > 0
        ? categoryFacets.categories.facets[0]
        : undefined;
});
